// Tree-structure substrate — read helpers, rebuild helpers, and the tree
// constructors. Pure.
//
// Split out of `Navigation/paneTree.ts` (C1 hotspot decomposition). Sits above
// `keys.ts`; the tree-level ops in the entry file are all `mapLeaf` over this.

import { keyOf, seedConversationTab, tabHasKey } from "./keys.js";
import {
    type Leaf,
    MIN_PANE_FRACTION,
    type Pane,
    type PaneId,
    type PaneTree,
    type Tab,
    type TabKey,
} from "./types.js";

// === Read helpers (pure) — the shell renders + measures with these ==========

// Every leaf, in stable in-order DFS order (strip order across panes). The
// order the flat focus-ring and the "first surviving leaf" fallback use.
export const leavesOf = (pane: Pane): readonly Leaf[] =>
    pane.type === "leaf" ? [pane] : pane.children.flatMap(leavesOf);

export const findLeaf = (pane: Pane, id: PaneId): Leaf | undefined =>
    leavesOf(pane).find((l) => l.id === id);

// The leaf that currently holds `key`, if any — the tree-wide dedup lookup.
export const leafIdOfKey = (pane: Pane, key: TabKey): PaneId | undefined =>
    leavesOf(pane).find((l) => l.tabs.some((t) => tabHasKey(t, key)))?.id;

const seedLeaf = (): Leaf => {
    const conversation = seedConversationTab();
    return {
        type: "leaf",
        id: "pane-0",
        tabs: [conversation],
        preview: null,
        active: { type: "conversation", id: conversation.id },
    };
};

// The focused leaf, or the first leaf when the pointer is stale.
export const focusedLeaf = (t: PaneTree): Leaf =>
    findLeaf(t.root, t.focused) ?? leavesOf(t.root)[0] ?? seedLeaf();

// === Constructors ============================================================

// A single-leaf tree holding one fresh nascent conversation (the landing) — the
// empty (never-visited) tree, and the shape every migrated tree starts as
// (behaviourally identical to `emptyTabs`). No pinned home any more.
export const seedPaneTree = (paneId: PaneId): PaneTree => {
    const conversation = seedConversationTab();
    return {
        root: {
            type: "leaf",
            id: paneId,
            tabs: [conversation],
            preview: null,
            active: { type: "conversation", id: conversation.id },
        },
        focused: paneId,
    };
};

// A single-leaf tree from an explicit working set — the bridge from a restored
// TabSet to a PaneTree (one strip = one leaf). An empty working set seeds a
// fresh nascent conversation.
export const singleLeafTree = (
    paneId: PaneId,
    tabs: readonly Tab[],
    active: TabKey,
): PaneTree => {
    const first = tabs[0];
    if (first === undefined) return seedPaneTree(paneId);
    return {
        root: {
            type: "leaf",
            id: paneId,
            tabs,
            preview: null,
            active: tabs.some((t) => tabHasKey(t, active))
                ? active
                : keyOf(first),
        },
        focused: paneId,
    };
};

// === Tree rebuild helpers ====================================================

// Clamp each size positive, then re-normalise so Σ = 1.
export const normalizeSizes = (sizes: readonly number[]): number[] => {
    if (sizes.length === 0) return [];
    const positive = sizes.map((s) =>
        Number.isFinite(s) && s > 0 ? s : MIN_PANE_FRACTION,
    );
    const sum = positive.reduce((a, b) => a + b, 0);
    return sum > 0
        ? positive.map((s) => s / sum)
        : positive.map(() => 1 / positive.length);
};

// Rebuild the tree, replacing the leaf `id` by `f(leaf)`. When `f` returns null
// the leaf is PRUNED: its parent branch drops the child, redistributes the
// freed size, and collapses to the surviving child when only one remains
// (invariants: no empty leaf, no 1-child branch). Same reference when `f` was a
// no-op. Returns null only if the whole subtree pruned away — the top-level
// folds guard against that reaching the root.
export const mapLeaf = (
    pane: Pane,
    id: PaneId,
    f: (l: Leaf) => Leaf | null,
): Pane | null => {
    if (pane.type === "leaf") return pane.id === id ? f(pane) : pane;
    let changed = false;
    const kept: Pane[] = [];
    const keptSizes: number[] = [];
    pane.children.forEach((child, i) => {
        const size = pane.sizes[i] ?? 1 / pane.children.length;
        if (findLeaf(child, id) === undefined) {
            kept.push(child);
            keptSizes.push(size);
            return;
        }
        const next = mapLeaf(child, id, f);
        if (next !== child) changed = true;
        if (next === null) return; // drop child + its size
        kept.push(next);
        keptSizes.push(size);
    });
    if (!changed) return pane;
    if (kept.length === 0) return null;
    if (kept.length === 1) return kept[0] ?? null; // collapse
    return { ...pane, children: kept, sizes: normalizeSizes(keptSizes) };
};

// The focused pointer after a structural edit: keep it when its leaf survived,
// else fall to the first leaf.
export const reFocus = (root: Pane, focused: PaneId): PaneId =>
    findLeaf(root, focused) ? focused : (leavesOf(root)[0]?.id ?? focused);
